import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import { CSSTransition } from "react-transition-group";
import { Key } from "ts-key-enum";
import { c, t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconXClose from "../../../../img/icons/x-close.svg";
import { ISafeHTML, isoSafeHTML } from "../../../models/nominals";
import { ICompareTile } from "../../../models/product-compare";
import { useViewport } from "../../../store/common/hooks";
import { useAppSelector } from "../../../store/reducers";
import { focusElement, trapFocus } from "../../../utils/keyboardFocus";
// eslint-disable-next-line import/no-restricted-paths -- pre-existing cross-app dependency (Phase E exemption)
import { ProductCompareGrid__ColumnCard_Component } from "../../product-compare/ProductCompareGrid__ColumnCard/ProductCompareGrid__ColumnCardContent";
import { showFinanceBlock } from "../ConfiguratorPanelTrigger";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { IModularConfiguratorOptionSet } from "../models";

import styles from "./index.module.scss";

export const RootProductCompareDrawer = (props: {
    optionSet: IModularConfiguratorOptionSet;
    prefixNameCapped: string;
    panelClass: string;
    isPanelOpen: boolean;
    useCompareDrawerCenter?: boolean;
    /** Ref for the element that opened the drawer; focus is returned here on close (A360 #341). */
    triggerRef?: React.RefObject<HTMLElement | null>;
    onCloseButtonClick: (
        removeStaticFromBody: () => void,
        removePanelStateToBody: () => void,
        showFinanceBlock: () => void,
    ) => void;
}) => {
    const optionPanel = useRef<HTMLDivElement | null>(null);
    const closeButton = useRef<HTMLButtonElement | null>(null);

    const rootProducts = useAppSelector(
        (state) => state.configurator.entities.rootProducts,
    );
    const viewport = useViewport();
    const priceSelectValue = useAppSelector(
        (state) => state.productcompare.priceSelectValue,
    );

    const closePanel = () => {
        if (props.triggerRef?.current) {
            focusElement(props.triggerRef.current);
        }
        props.onCloseButtonClick(
            removeStaticFromBody,
            removePanelStateToBody,
            showFinanceBlock,
        );
    };

    const onEscKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === Key.Escape) {
            closePanel();
        }
    };

    const isMobileWidth = () => {
        const window = getWindow();
        return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE;
    };

    const addStaticToBody = () => {
        const document = getDocument();
        if (document) {
            document.body.classList.add("static");
        }
    };

    const removeStaticFromBody = () => {
        const document = getDocument();
        if (document) {
            document.body.classList.remove("static");
        }
    };

    const addPanelStateToBody = () => {
        const document = getDocument();
        if (document && !props.useCompareDrawerCenter) {
            document.body.classList.add("configurator-panel-open");
        }
    };

    const removePanelStateToBody = () => {
        const document = getDocument();
        if (document && !props.useCompareDrawerCenter) {
            document.body.classList.remove("configurator-panel-open");
        }
    };

    const onMouseEnterPanel = () => {
        if (optionPanel) {
            addStaticToBody();
        }
    };

    const onMouseLeavePanel = () => {
        if (optionPanel) {
            removeStaticFromBody();
        }
    };

    const getTiles = (): ICompareTile[] => {
        if (!props.optionSet.root_product_compare_modal) {
            return [];
        }
        const baseTiles = props.optionSet.root_product_compare_modal.products;
        // Annotate each tile with the related product data
        const tiles = baseTiles
            .map((baseTile): ICompareTile => {
                const rootProduct = rootProducts.find((rp) => {
                    return rp.id === baseTile.product_compare_tile.product_id;
                });
                return {
                    ...baseTile,
                    rootProducts: rootProduct ? [rootProduct] : [],
                };
            })
            .filter((tile) => tile.rootProducts.length > 0);
        return tiles;
    };

    const buildHeader = (): ISafeHTML => {
        if (!props.optionSet.root_product_compare_modal_title) {
            return isoSafeHTML.wrap("");
        }

        return isoSafeHTML.wrap(
            `<h3>${props.optionSet.root_product_compare_modal_title}</h3>`,
        );
    };

    useEffect(() => {
        // Wait for animation completed
        const timeoutRef = setTimeout(() => {
            if (closeButton?.current) {
                focusElement(closeButton.current);
            }
            if (optionPanel?.current) {
                trapFocus(optionPanel.current, {
                    includeExplicitTabindex: true,
                });
            }
        }, 500);

        if (isMobileWidth()) {
            addStaticToBody();
        }

        addPanelStateToBody();

        return () => {
            removeStaticFromBody();
            removePanelStateToBody();
            clearTimeout(timeoutRef);
        };
    }, [
        focusElement,
        trapFocus,
        isMobileWidth,
        addStaticToBody,
        addPanelStateToBody,
        removeStaticFromBody,
        removePanelStateToBody,
    ]);

    if (!props.optionSet.root_product_compare_modal) {
        return null;
    }
    const panelClasses = classNames({
        configurator__panel: true,
        [styles.panel]: !props.useCompareDrawerCenter,
        [styles.compareDrawerCenter]: props.useCompareDrawerCenter,
        [`configurator__panel--${props.panelClass}`]: true,
        [`configurator__panel--compare-drawer`]: true,
        [`configurator__panel--compare-drawer--center`]:
            props.useCompareDrawerCenter,
    });
    const tiles = getTiles();
    return (
        <>
            <CSSTransition
                in={props.isPanelOpen}
                timeout={500}
                classNames="configurator__panel-"
                unmountOnExit={true}
                nodeRef={optionPanel}
            >
                <div
                    aria-modal="true"
                    aria-label={c("RootProductCompareDrawer")
                        .t`${props.prefixNameCapped} option selection panel`}
                    className={panelClasses}
                    ref={optionPanel}
                    role="dialog"
                    onKeyDown={onEscKeyDown}
                    onMouseEnter={onMouseEnterPanel}
                    onMouseLeave={onMouseLeavePanel}
                >
                    <div className={styles.root}>
                        <div
                            className={`${styles.head} configurator__panel--head`}
                        >
                            {" "}
                            <h5>Compare</h5>
                            <button
                                type="button"
                                ref={closeButton}
                                aria-label={t`Close`}
                                onClick={closePanel}
                            >
                                <SVG aria-hidden="true" src={iconXClose} />
                            </button>
                        </div>
                        {/* wrapping with the same class as the Wagtail Block Variant to ensure proper styling */}
                        <div
                            className={`${styles.grid} product-compare-grid-column-card`}
                        >
                            <ProductCompareGrid__ColumnCard_Component
                                header={buildHeader()}
                                tiles={tiles}
                                priceSelectValue={priceSelectValue}
                                viewport={viewport}
                            />
                        </div>
                    </div>
                </div>
            </CSSTransition>
        </>
    );
};
